// Repository — one row per forge repo we're enabled on, qualified by which forge
// (#3984, plan 101 slice 06/07b). Every row is GitHub today; the qualifier rides on
// the entity so a GitHub-only lane can REFUSE a row that is not, rather than act on
// it with a GitHub App token.
// Policy — parsed `.maintainer.yml` snapshot. Immutable per `sourceSha`.

import { z } from 'zod';
import { FORGES } from '../value-objects/forge';
import { AccountIdSchema, PolicyIdSchema, RepoIdSchema } from '../value-objects/ids';

/**
 * The two non-null values of `repositories.ai_first_source` — HOW a repo became
 * onboarded for AI-first work.
 *
 * HERE, not beside the writers, because the column is plain `text` with no CHECK
 * and no NOT NULL: nothing in Postgres refuses a third value, so the vocabulary
 * has to have exactly ONE definition in the codebase or the writer and the reader
 * drift apart silently. It previously lived as two module-private constants in
 * `packages/db/src/queries/repositories.ts`, which meant the only reader that
 * could ever check them had to retype them — and a retyped literal that stops
 * matching does not fail, it reports every earned repo as an unknown source.
 * `@developerz/domain` imports nothing internal, so the db writers and the
 * `bun run infra onboarding` probe can both read this one array.
 *
 * Null is the third state and is NEVER written: migration `0149` backfilled
 * `ai_first_at` over every row that existed and `0170` added the source column
 * afterwards, so `ai_first_at IS NOT NULL AND ai_first_source IS NULL` means
 * GRANDFATHERED — a marker whose origin is unknowable retroactively, and a
 * different fact from "earned a pass". Not on {@link RepositorySchema}: no
 * runtime caller reads the source through the entity, only `bun run infra
 * onboarding` reads it at all, and it reads the column directly.
 */
export const AI_FIRST_SOURCES = ['passed_onboarding', 'manual_declaration'] as const;

/** One of {@link AI_FIRST_SOURCES}. Null (grandfathered) is modelled separately. */
export type AiFirstSource = (typeof AI_FIRST_SOURCES)[number];

export const RepositorySchema = z.object({
  id: RepoIdSchema,
  accountId: AccountIdSchema,
  githubRepoId: z.number().int().positive(),
  /**
   * WHICH FORGE `githubRepoId` and `fullName` belong to (`repositories.forge`,
   * migration 0324). Every stored row reads `github` today.
   *
   * REQUIRED and NOT defaulted, the same stance `private` / `donationsEnabled` /
   * `canaryEnabled` / `publicStatsEnabled` take below, and for a sharper reason:
   * this field's whole job is to let a GitHub-only lane refuse a row it must not
   * touch, and a default of `'github'` is that refusal failing OPEN. A row whose
   * forge went missing anywhere between the column and the fence would read as
   * GitHub and be acted on with a GitHub App token — the exact failure slice 07b
   * exists to prevent. Optional would be worse again: every fence would have to
   * decide what `undefined` means, which is a branch nobody tested.
   *
   * The vocabulary is `FORGES` (`value-objects/forge.ts`), ONE definition shared
   * with the SQL writers; the column's CHECK is built from the same array.
   */
  forge: z.enum(FORGES),
  /**
   * The forge INSTANCE's host (`repositories.instance`) — `github.com`, or a
   * self-hosted GitLab's host. Part of the stored uniqueness key, because GitLab
   * ids are per instance: `(forge, native id)` is under-keyed by one column.
   *
   * A plain non-empty host string rather than an enum: the set of self-hosted
   * instances is open, so there is nothing to close it against. Required for the
   * same fail-open reason as {@link RepositorySchema} `forge` — `forge = 'gitlab'`
   * alone does not say WHICH GitLab, and two of them reuse ids.
   */
  instance: z.string().min(1),
  fullName: z.string().regex(/^[^/]+\/[^/]+$/, 'expected "owner/repo"'),
  defaultBranch: z.string().min(1),
  enabled: z.boolean(),
  /**
   * GitHub visibility — the free-tier line (arc 101 slice 01). Free covers your
   * OWN PUBLIC repos; a private repo needs a paid seat + a box. Truth lives on
   * GitHub and is MUTABLE (the `repository` `publicized`/`privatized` webhook
   * flips it), so no read may cache it.
   *
   * Required, not defaulted: the tier is an ACCOUNT fact while visibility is a
   * REPO fact, and one org account legitimately runs free public repos beside
   * paid private ones. Every per-repo decision — enablement, the donated-resource
   * fence, public stats — keys off this field, so a caller must state it rather
   * than inherit a guess.
   */
  private: z.boolean(),
  /**
   * The owner's opt-in to RECEIVE donations (arc 101 slice 02). Necessary, not
   * sufficient: a donatable repo is `enabled AND NOT private AND donationsEnabled`,
   * so a repo that goes private drops out of every donatable read with no cleanup.
   *
   * Deliberately NOT part of `.maintainer.yml` — flipping it publishes this repo's
   * activity on a public page + backer wall, which is an account/billing choice,
   * not repo policy (`docs/idea/principles.md`).
   */
  donationsEnabled: z.boolean(),
  /**
   * The owner's opt-in to the daily CANARY (`repositories.canary_enabled`, 0257):
   * one small, safe, real ask filed per UTC day as an end-to-end health probe
   * (`value-objects/repo-canary.ts`). Off by default at the column; required here,
   * like `donationsEnabled`, so a caller states it rather than inheriting a guess.
   * Necessary, not sufficient: the daily scan also requires `enabled`.
   */
  canaryEnabled: z.boolean(),
  /**
   * The owner's CONSENT to publish this repo's agent-authored health numbers on
   * the public stats route (`repositories.public_stats_enabled`, 0318, #5660).
   * Off by default at the column; required here, like `donationsEnabled`, so a
   * caller states it rather than inheriting a guess. Consent, not eligibility:
   * the route's gate also requires `enabled` and `not private`.
   */
  publicStatsEnabled: z.boolean(),
  /**
   * When repo onboarding LOOKED at this repo and found it fit for an agent to
   * work in (`docs/idea/repo-onboarding.md`). Null = never onboarded, and the
   * claim gate holds every `outcome_kind = 'pr'` task on the repo — coding work
   * only; triage, review, releases, `report` and `ops_action` are unaffected.
   *
   * A TIMESTAMP rather than a boolean because the three writers all answer
   * "when did somebody look": a `ready` verdict from the pass, the
   * `dz/onboarding` PR merging, or the maintainer declaring it over
   * `PATCH /v1/repos/:o/:r`. Idempotent by construction — the first stamp wins
   * and a redelivery is a no-op, so "when" survives a re-run that "true" would
   * silently rewrite.
   */
  aiFirstAt: z.date().nullable(),
  policyId: PolicyIdSchema.nullable(),
  policyHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'expected sha256 hex')
    .nullable(),
  createdAt: z.date(),
});
export type Repository = z.infer<typeof RepositorySchema>;

// `PolicySchema`, `HandoffMode`, and `handoffReferenceConsistent` used to live
// here as a second declaration of the `.maintainer.yml` contract. They drifted:
// the canonical contract (`fleet` mode added in #1026, `label` companion, …) is
// owned by `@developerz/policy`, and no runtime caller imports these from
// `@developerz/domain` (every call site — apps/api, apps/worker,
// apps/agents-manager, packages/agent — pulls `PolicySchema`/`validate` from
// `@developerz/policy`). The duplicate could not be caught by types — both are
// Zod enums over string literals, so a missing mode silently rejected every
// `fleet` repo at parse time the moment any consumer started parsing through
// the domain copy. One owner per contract: deleted here.
